import {
  useCreateExhibitFileMutation,
  useUpdateExhibitByIdMutation,
  useUpdateOnboardingByIdMutation,
} from "@codegen/index";
import {
  CreateExhibitFileInput,
  UpdateExhibitByIdInput,
  UpdateOnboardingByIdInput,
} from "@codegen/schema";
import {
  OnboardingContext,
  onboardingMachine,
} from "@pages/onboarding/beneficiary/machines";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useBeneficiaryWorkflowContext } from "@pages/onboarding/workflows/provider";
import { MODULES_WITH_COVER_FALLBACKS } from "@utils/constants";
import { useLogError } from "@utils/error";
import {
  PlymouthUser,
  StrategyExhibit,
  StrategyModule,
  Team,
} from "@utils/types";
import { createActorContext } from "@xstate/react";
import * as React from "react";

export const BeneficiaryOnboardingContext =
  createActorContext(onboardingMachine);

// Helper type to ensure files property exists on form data
type FormDataWithFiles = {
  files?: string[];
};

type OnboardingFormDataType = Record<string, unknown>;

// Create a context for the onboarding state and actions
interface OnboardingProviderContextType {
  // State
  modules?: StrategyModule[];
  currentModule?: StrategyModule;
  currentExhibit?: StrategyExhibit;
  currentModuleIndex: number;
  currentExhibitIndex: number;
  currentStep: number;
  totalSteps: number;
  moduleProgress: number;
  isSaving: boolean;

  // Actions
  handleNext: (data?: OnboardingFormDataType) => void;
  handlePrev: () => void;
  handleSkip: () => void;
  handleSkipForNow: () => void;
  handleSave: (data?: OnboardingFormDataType) => Promise<void>;
  saveExhibitData: (
    module: any,
    exhibit: any,
    data: OnboardingFormDataType
  ) => void;

  // Context
  petition?: OnboardingPageType["petition"];
  onboarding?: OnboardingPageType["onboarding"];
  company: Team;
  contact: PlymouthUser;
}

const OnboardingProviderContext = React.createContext<
  OnboardingProviderContextType | undefined
>(undefined);
// This component provides the state machine context
const StateMachineProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <BeneficiaryOnboardingContext.Provider>
      {children}
    </BeneficiaryOnboardingContext.Provider>
  );
};

// This component provides the onboarding context with all the actions and state
export const OnboardingStateMachineProvider = ({
  children,
  petition,
  company,
  contact,
  onboarding,
  modules,
  defaultModuleIndex = 0,
  defaultExhibitIndex = 0,
}: {
  children: React.ReactNode;
  petition?: OnboardingPageType["petition"];
  company: Team;
  contact: PlymouthUser;
  onboarding?: OnboardingPageType["onboarding"];
  modules?: StrategyModule[];
  defaultModuleIndex?: number;
  defaultExhibitIndex?: number;
}) => {
  // Wrap children with the StateMachineProvider first
  return (
    <StateMachineProvider>
      {/* Then render the context provider which uses the actor */}
      <OnboardingContextProvider
        petition={petition}
        company={company}
        contact={contact}
        onboarding={onboarding}
        modules={modules}
        defaultModuleIndex={defaultModuleIndex}
        defaultExhibitIndex={defaultExhibitIndex}
      >
        {/* Finally, render the children inside both providers */}
        {children}
      </OnboardingContextProvider>
    </StateMachineProvider>
  );
};

// This component provides the actual implementation of the onboarding context
const OnboardingContextProvider = ({
  children,
  petition,
  company,
  contact,
  onboarding,
  modules,
  defaultModuleIndex = 0,
  defaultExhibitIndex = 0,
}: {
  children: React.ReactNode;
  petition?: OnboardingPageType["petition"];
  company: Team;
  contact: PlymouthUser;
  onboarding?: OnboardingPageType["onboarding"];
  modules?: StrategyModule[];
  defaultModuleIndex?: number;
  defaultExhibitIndex?: number;
}) => {
  const actor = BeneficiaryOnboardingContext.useActorRef();
  const [currentContext, setCurrentContext] = React.useState<
    OnboardingContext | undefined
  >();
  const logError = useLogError();
  const [isSaving, setIsSaving] = React.useState(false);

  const [saveOnboarding] = useUpdateOnboardingByIdMutation();
  const [saveExhibit] = useUpdateExhibitByIdMutation();
  const [addFileToExhibit] = useCreateExhibitFileMutation();
  const { refetchOnboarding } = useBeneficiaryWorkflowContext();

  // Derived state
  const currentModuleIndex = currentContext?.currentModuleIndex ?? 0;
  const currentExhibitIndex = currentContext?.currentExhibitIndex ?? 0;
  const machineModules = currentContext?.modules;
  const currentModule = machineModules?.[currentModuleIndex];
  const currentStep = currentContext?.currentStep ?? 0;
  const totalSteps = currentContext?.totalSteps ?? 0;
  const moduleProgress = currentContext?.moduleProgress ?? 0;

  // Calculate current exhibit with fallback for cover pages
  const currentExhibit: StrategyExhibit | undefined = React.useMemo(() => {
    if (
      currentExhibitIndex === -1 &&
      currentModule &&
      currentModule.exhibits.length > 0 &&
      MODULES_WITH_COVER_FALLBACKS.includes(currentModule.type) &&
      !currentModule.exhibits.some((exhibit) => exhibit.type === "COVER_PAGE")
    ) {
      let description = "";
      switch (currentModule.type) {
        case "CRITICAL_ROLE":
          description = "Critical Role Description";
          break;
        case "HIGH_REMUNERATION":
          description = "High Remuneration Description";
          break;
        case "ORIGINAL_CONTRIBUTION":
          description = "Original Contribution Evidence Description";
          break;
        case "MEMBERSHIP":
          description = "Membership Documentation Description";
          break;
        case "PRESS":
          description = "Press Article Description";
          break;
        case "AWARDS":
          description = "Award Description";
          break;
        case "AUTHORSHIP":
          description = "Authorship Major Publication Description";
          break;
        case "JUDGING":
          description = "Judging Description";
          break;
        default:
          description = "";
          break;
      }

      return {
        id: "cover-page",
        status: "pending",
        data: {},
        type: "COVER_PAGE",
        description: description,
        name: "",
        required: false,
      } as StrategyExhibit;
    }
    return currentModule?.exhibits[currentExhibitIndex ?? 0];
  }, [currentExhibitIndex, currentModule]);

  // Initialize the state machine
  React.useEffect(() => {
    if (modules) {
      actor.send({
        type: "INIT_MODULES",
        modules,
        defaultModuleIndex,
        defaultExhibitIndex,
      });

      const subscription = actor.subscribe((snapshot) => {
        setCurrentContext(snapshot.context);
        savePayload(snapshot);
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [modules, actor, defaultModuleIndex, defaultExhibitIndex]);

  // Save the current state to the backend
  const savePayload = async (
    snapshot?: ReturnType<typeof actor.getSnapshot>
  ) => {
    const contextSnapshot = snapshot ?? actor.getSnapshot();
    if (
      contextSnapshot.context?.modules &&
      contextSnapshot.context?.modules?.length > 0 &&
      onboarding?.id
    ) {
      try {
        await saveOnboarding({
          variables: {
            input: {
              id: onboarding.id,
              onboardingPatch: {
                modules: JSON.stringify(contextSnapshot.context.modules),
              },
            } as UpdateOnboardingByIdInput,
          },
        });

        await refetchOnboarding();

        const viewTeamId = localStorage.getItem("viewTeam");
        const viewAccountId = localStorage.getItem("viewAccountId");
        localStorage.setItem(
          "onboardingState" + viewTeamId + viewAccountId,
          JSON.stringify(contextSnapshot.context.modules)
        );
        localStorage.setItem(
          "currentModuleIndex" + viewTeamId + viewAccountId,
          contextSnapshot.context.currentModuleIndex.toString()
        );
        localStorage.setItem(
          "currentExhibitIndex" + viewTeamId + viewAccountId,
          contextSnapshot.context.currentExhibitIndex.toString()
        );
      } catch (error) {
        logError(error, {
          petitionId: petition?.id,
          onboardingId: onboarding?.id,
          contactId: contact.id,
          companyId: company.value,
        });
      }
    }
  };

  // Save exhibit data to the state machine
  const saveExhibitData = (
    module: any,
    exhibit: any,
    data: OnboardingFormDataType
  ) => {
    actor.send({
      type: "SET_DATA",
      moduleId: module.id,
      exhibitId: exhibit?.id,
      data,
    });
  };

  /**
   * Handles saving exhibit metadata and uploading associated files
   * @param exhibitId The ID of the exhibit
   * @param data Form data containing metadata and possibly files
   */
  const saveExhibitMetadataAndFiles = async (
    exhibitId: number,
    data: OnboardingFormDataType
  ) => {
    try {
      // Save exhibit metadata
      await saveExhibit({
        variables: {
          input: {
            clientMutationId: "",
            id: exhibitId.toString(),
            exhibitPatch: {
              metadata: JSON.stringify(data),
            },
          } as UpdateExhibitByIdInput,
        },
      });

      // Handle file uploads if present
      const filesData = data as FormDataWithFiles;
      if (
        filesData.files &&
        Array.isArray(filesData.files) &&
        filesData.files.length > 0
      ) {
        for (const fileId of filesData.files) {
          try {
            await addFileToExhibit({
              variables: {
                input: {
                  clientMutationId: "",
                  exhibitFile: {
                    fileId: fileId,
                    exhibitId: exhibitId,
                  },
                } as unknown as CreateExhibitFileInput,
              },
            });
          } catch (error) {
            logError(error, {
              message: "Error adding file to exhibit",
              exhibitId: exhibitId,
              fileId: fileId,
            });
          }
        }
      }
    } catch (error) {
      logError(error, {
        message: "Error saving exhibit metadata or files",
        exhibitId: exhibitId,
      });
    }
  };

  // Action handlers
  const handleSave = async (data?: OnboardingFormDataType) => {
    if (data && currentModule && currentExhibit) {
      setIsSaving(true);
      actor.send({
        type: "SET_DATA",
        moduleId: currentModule?.id,
        exhibitId: currentExhibit?.id,
        data,
        status: "completed",
      });
      try {
        // Try to save exhibit metadata and upload files
        if (currentExhibit.exhibitId) {
          await saveExhibitMetadataAndFiles(currentExhibit.exhibitId, data);
          setIsSaving(false);
        } else {
          setIsSaving(false);
        }
      } catch (error) {
        logError(error, {
          petitionId: petition?.id,
          onboardingId: onboarding?.id,
          contactId: contact.id,
          companyId: company.value,
          exhibitId: currentExhibit.id,
          moduleId: currentModule.id,
        });
      }
    }
  };

  const handleNext = (data?: OnboardingFormDataType) => {
    // If we're at the special -1 index for a module that supports it, just move to the next exhibit
    if (currentExhibitIndex === -1 && currentModule) {
      actor.send({ type: "NEXT_EXHIBIT" });
      return;
    }

    // If we have data to save and we're not at the special -1 index
    if (data && currentModule && currentExhibit) {
      setIsSaving(true);
      actor.send({
        type: "SET_DATA",
        moduleId: currentModule?.id,
        exhibitId: currentExhibit?.id,
        data,
        status: "completed",
      });

      try {
        // Try to save exhibit metadata and upload files
        if (currentExhibit.exhibitId) {
          saveExhibitMetadataAndFiles(currentExhibit.exhibitId, data).then(
            () => {
              setIsSaving(false);
            }
          );
        } else {
          setIsSaving(false);
        }
      } catch (error) {
        logError(error, {
          petitionId: petition?.id,
          onboardingId: onboarding?.id,
          contactId: contact.id,
          companyId: company.value,
          exhibitId: currentExhibit.id,
          moduleId: currentModule.id,
        });
      }
    }

    actor.send({ type: "NEXT_EXHIBIT" });
  };

  const handleSkip = () => {
    // If we're at the special -1 index for a module that supports it, just move to the next exhibit
    if (currentExhibitIndex === -1 && currentModule) {
      actor.send({ type: "NEXT_EXHIBIT" });
      return;
    }

    // If we have an exhibit to skip and we're not at the special -1 index
    if (currentExhibit && currentModule) {
      actor.send({
        type: "SET_EXHIBIT_STATUS",
        moduleId: currentModule?.id,
        exhibitId: currentExhibit?.id,
        status: "skipped",
      });
    }

    actor.send({ type: "NEXT_EXHIBIT" });
  };

  const handleSkipForNow = () => {
    // If we're at the special -1 index for a module that supports it, just move to the next exhibit
    if (currentExhibitIndex === -1 && currentModule) {
      actor.send({ type: "NEXT_EXHIBIT" });
      return;
    }

    // If we have an exhibit to skip and we're not at the special -1 index
    if (currentExhibit && currentModule) {
      actor.send({
        type: "SET_EXHIBIT_STATUS",
        moduleId: currentModule?.id,
        exhibitId: currentExhibit?.id,
        status: "pending",
      });
    }

    actor.send({ type: "NEXT_EXHIBIT" });
  };

  const handlePrev = () => {
    actor.send({ type: "PREV_EXHIBIT" });
  };

  const contextValue: OnboardingProviderContextType = {
    currentModule,
    currentExhibit,
    currentModuleIndex,
    currentExhibitIndex,
    currentStep,
    totalSteps,
    moduleProgress,
    isSaving,
    handleNext,
    handlePrev,
    handleSkip,
    handleSkipForNow,
    handleSave,
    saveExhibitData,
    petition,
    onboarding,
    company,
    contact,
    modules,
  };

  return (
    <OnboardingProviderContext.Provider value={contextValue}>
      {children}
    </OnboardingProviderContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = React.useContext(OnboardingProviderContext);
  if (context === undefined) {
    throw new Error(
      "useOnboarding must be used within an OnboardingStateMachineProvider"
    );
  }
  return context;
};

export const useStateMachine = () => BeneficiaryOnboardingContext;
